<template>
  <div class="stack-lg">
    <div class="text-right" v-if="id">
      <wd-button
        transparent
        @input="finishSurvey"
        v-if="formData.status === 'pending'"
      >{{ $t("surveys.manage.finish_survey") }}</wd-button>
      <wd-button transparent @input="reopenSurvey" v-else>
        {{
        $t("surveys.manage.reopen_survey")
        }}
      </wd-button>
    </div>
    <div class="stack-lg" v-if="!formData.status || formData.status === 'pending'">
      <form @submit.prevent="submit(formOptions)">
      <div class="stack">
        <div class="stack">
          <input-text
            required
            :value="formData.question"
            label="Frage"
            @update-value="updateForm('question', $event, id || 'NewSurvey')"
          />
          <input-select
            required
            :label="$t('general.category')"
            :options="surveyTypes"
            optionlabel="title"
            optionvalue="id"
            :value="formData.type"
            @update-value="updateForm('type', $event, id || 'NewSurvey')"
          />
        </div>
        <div>
          <wd-button warning @input="sendDelete" :sending="sending">
            {{
            $t("general.delete")
            }}
          </wd-button>
          <wd-button
            class="ml-2"
            propagate
            :sending="sending"
          >{{ id ? $t("general.update") : $t("general.create") }}</wd-button>
        </div>
      </div>
      </form>
      <div class="stack" v-if="id">
        <div
          v-if="surveyOptions.length < 1 && unsavedOptions.length < 1"
        >{{ $t("surveys.manage.no_options_yet") }}</div>
        <div class="stack" v-else>
          <survey-option
            v-for="option in surveyOptions"
            :key="option.id"
            :option="option"
            :survey-id="id"
            @tab-key="addOption"
          />
          <survey-option
            v-for="(option, index) in unsavedOptions"
            :key="index"
            :option="option"
            :survey-id="id"
            unsaved
            @remove="removeSurveyOption(index)"
          />
        </div>
        <div v-if="id">
          <wd-button icon="Plus" @input="addOption">
            {{
            $t("surveys.manage.add_option")
            }}
          </wd-button>
        </div>
      </div>
    </div>
    <div class="p-4 border border-gray-400" v-if="id && surveyOptions.length > 0">
      <h2 class="mb-1">
        {{
        formData.status === "pending"
        ? $t("surveys.intermediate_result")
        : $t("surveys.result")
        }}
      </h2>
      <p class="italic mb-3">{{ $t(`surveys.type.${formData.type}`) }}</p>
      <p v-if="votes">
        {{
        votes.length +
        " " +
        $tc("surveys.votes_given", votes.length)
        }}
      </p>
      <div class="my-4 stack-sm" v-if="surveyOptions.length > 0">
        <div class="p-2" v-for="option in surveyOptions" :key="option.id">
          <div class="text-sm">
            <span class="mr-1 font-medium">{{ getOptionEntriesCount(option.id) }}%</span>
            {{ option.text }}
          </div>
          <div class="w-full">
            <progress-bar :amount="getOptionEntriesCount(option.id)" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FormMixin from "@/mixins/FormMixin";
import { SurveyOption as SurveyOptionModel } from "@/modules/surveys/viewmodels/SurveyOption";

const SurveyOption = require("@/modules/surveys/components/SurveyOption.vue")
  .default;
const ProgressBar = require("@/modules/core/components/ProgressBar.vue")
  .default;

export default {
  name: "SurveyManageSite",
  mixins: [FormMixin],
  data() {
    return {
      surveyTypes: [
        {
          title: "Single Choice",
          id: "single"
        },
        {
          title: "Multiple Choice",
          id: "multiple"
        }
      ],
      unsavedOptions: [],
      optionEntries: null,
      votes: null
    };
  },
  props: {
    id: {
      type: String
    }
  },
  components: {
    SurveyOption,
    ProgressBar
  },
  methods: {
    addOption() {
      this.unsavedOptions.push(new SurveyOptionModel({}));
    },
    removeSurveyOption(Index) {
      this.unsavedOptions.splice(Index, 1);
    },
    sendDelete() {
      this.$store.dispatch("dialog/add", {
        title: "Löschen bestätigen",
        text: `Gesamten Artikel wirklich löschen?`,
        submitFn: () => {
          this.deleteSurvey();
        }
      });
    },
    getOptionEntriesCount(Id) {
      return this.optionEntries && this.optionEntries.length > 0
        ? Math.round(
            (this.optionEntries.filter(e => e.survey_option_id === Id).length /
              this.optionEntries.length) *
              100
          )
        : 0;
    },
    finishSurvey() {
      let finishSurveyFormOptions = {
        async: true,
        type: "survey",
        call: "update",
        id: this.id,
        data: { status: "done" },
        sendingVar: false,
        action: response => {
          this.formData.status = "done";
          this.popup({
            title: this.$t("surveys.manage.survey_finished"),
            autohide: true
          });
        }
      };

      this.submit(finishSurveyFormOptions);
    },
    reopenSurvey() {
      let reopenSurveyFormOptions = {
        async: true,
        type: "survey",
        call: "update",
        id: this.id,
        data: { status: "pending" },
        sendingVar: false,
        action: response => {
          this.formData.status = "pending";
          this.popup({
            title: this.$t("surveys.manage.survey_reopened"),
            autohide: true
          });
        }
      };

      this.submit(reopenSurveyFormOptions);
    },
    deleteSurvey() {
      let deleteFormOptions = {
        async: true,
        type: "survey",
        call: "destroy",
        sendingVar: false,
        id: this.id,
        action: response => {
          this.deleteSending = false;
          this.goTo("/surveys");
          this.popup({
            title: this.$t("surveys.manage.survey_deleted"),
            autohide: true
          });
        }
      };

      this.submit(deleteFormOptions);
    }
  },
  computed: {
    surveyOptions() {
      return this.$store.getters["surveyoption/surveyOptionsOfSurvey"](this.id);
    }
  },
  async created() {
    if (this.id) {
      this.formData = await this.$store.getters["survey/api"].Show(this.id);
      await this.$store.getters["surveyoption/api"].Custom.ShowOptionsOfSurvey(
        this.id
      );

      this.optionEntries = await this.$store.getters[
        "survey/api"
      ].Custom.OptionEntries(this.id);

      this.votes = await this.$store.getters["survey/api"].Custom.Votes(
        this.id
      );

      this.formOptions = {
        async: true,
        type: "survey",
        call: "update",
        id: this.id,
        action: response => {
          this.$store.dispatch("temp/removeUnsavedItem", {item: this.id});
          this.popup({
            title: this.$t("surveys.manage.survey_updated"),
            autohide: true
          });
        }
      };
    } else {
      this.formOptions = {
        async: true,
        type: "survey",
        call: "create",
        id: this.id,
        action: response => {
          this.$store.dispatch("temp/removeUnsavedItem", {item: 'NewSurvey'});
          this.goTo("/survey-manage/" + response.id);
          this.popup({
            title: this.$t("surveys.manage.survey_created"),
            autohide: true
          });
        }
      };
    }

    this.loaded();
  },
  watch: {
    id: {
      async handler(Val) {
        if (Val) {
          this.formData = await this.$store.getters["survey/api"].Show(Val);
          await this.$store.getters[
            "surveyoption/api"
          ].Custom.ShowOptionsOfSurvey(Val);
        }
      },
      immediate: true
    }
  }
};
</script>
